import React, { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import ReactEcharts from "echarts-for-react";

import { groupArr } from "../../util/formatter";
import { useSite } from "./../../util/sites";
import { useSolarCalc } from "../../util/calculations";
import GYAxios from "../../util/GYAxios";

const MapkwpGraph = (props) => {
	const currentSite = useSite();
	const SC = useSolarCalc();
	const [selectedSite, setSelectedSite] = useState(null);
	const [kWPValues, setKWPValues] = useState(null);
	const [graphOpts, setGraphOpts] = useState(null);
	const [selectAll, setSelectAll] = useState(true);

	/**
	 * Format kWPeak entries
	 * @param {obj} obj
	 * @returns formatter data entries
	 */
	const getKWPValues = (obj) => {
		const inverterName = obj.inv_no.replace("-", " ");
		const entry = [inverterName, obj.kwp];

		return entry;
	};

	/**
	 * GEnerate graph options and build graph
	 * @param {array} graphData
	 */
	const handleGraphData = (graphData) => {
		const entries = Object.entries(graphData);
		const dataEntries = entries.filter((entry) => entry[0].includes("INV"));
		const dates = entries.filter((entry) => entry[0] === "dateData");

		// kW/kWPeak entries
		const kWPerkWPEntries = dataEntries.map((entry) => {
			const inverterName = entry[0];
			const inverterP10 = entry[1];
			const kWP = kWPValues[inverterName];

			const kWPerkWP = inverterP10.map((kW) =>
				kWP != 0 ? SC.parseFormatNumber(kW / kWP, 3) : 0
			);

			const finalEntry = [inverterName, kWPerkWP];
			return finalEntry;
		});

		// Merge data and dateData
		const finalEntries = [...dates, ...kWPerkWPEntries];
		const finalDataObj = Object.fromEntries(finalEntries);

		const graphOpts = buildGraphOpts(finalDataObj);

		setGraphOpts(graphOpts);
	};

	/**
	 * Get graph options
	 * @param {array} graphData
	 * @returns Graph options
	 */
	const buildGraphOpts = (graphData) => {
		const series = buildSeries(graphData);
		const legends = Object.keys(graphData);
		const dateData = graphData.dateData;
		const selected = Object.fromEntries(
			legends.map((invName) => [invName, selectAll])
		);

		const optionInv = {
			title: {
				text: "kW/kWPeak Graph",
			},
			legend: {
				selected,
				data: legends,
				itemHeight: 17,
				type: "scroll",
				orient: "vertical",
				right: 20,
				top: 10,
			},
			grid: {
				top: 60,
				width: "80%",
			},
			tooltip: {
				trigger: "axis",
				formatter: function (params) {
					let rez = "";

					// Group inverters to groups of 10
					const tempArr = groupArr(params, 10);
					rez += "<p><b>Date Time - " + params[0].axisValue + " </b></p>";
					// Build each column with the array of groups of 10
					tempArr.map((col) => {
						rez +=
							"<div style='display:inline-block;margin: 0 5px; vertical-align:top'>";
						col.map((row) => {
							rez +=
								"<p>" +
								'<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
								row.color +
								'"></span>' +
								row.seriesName +
								": " +
								row.value +
								"</p>";
						});
						rez += "</div>";
					});
					return rez;
				},
			},
			axisPointer: {
				label: {
					type: "shadow",
				},
			},
			dataZoom: [
				{
					type: "slider",
					filterMode: "filter",
					start: 90,
					end: 100,
				},
				{
					type: "inside",
					filterMode: "filter",
					realtime: true,
					start: 90,
					end: 100,
				},
			],
			dataset: {
				source: dateData,
			},
			xAxis: {
				type: "category",
				axisTick: {
					alignWithLabel: true,
				},
				axisLine: {
					onZero: false,
					lineStyle: {},
				},
				axisTick: {
					show: true,
				},
				data: dateData,
			},
			yAxis: {
				name: "kWPeak",
				nameTextStyle: {
					fontWeight: "bold",
				},
				axisLine: {
					show: true,
				},
				axisTick: {
					show: true,
				},
				axisLabel: {
					formatter: "{value}",
				},
			},
			series: series,
		};

		return optionInv;
	};

	/**
	 * Get series obj of graph options
	 * @param {array} graphData
	 * @returns Graph series object
	 */
	const buildSeries = (graphData) => {
		const entries = Object.entries(graphData);
		const dataEntries = entries.filter((entry) => entry[0].includes("INV"));

		const series = dataEntries.map((entry) => {
			const invName = entry[0];
			const data = entry[1];

			const singleSeries = {
				name: invName,
				type: "line",
				seriesLayoutBy: "row",
				lineStyle: {
					normal: {
						width: 1.2,
					},
				},
				symbolSize: 2,
				showSymbol: false,
				smooth: false,
				data: data,
			};

			return singleSeries;
		});

		return series;
	};

	useEffect(() => {
		if (
			selectedSite == null ||
			(currentSite.selectedSite != null &&
				selectedSite.value.siteId != currentSite.selectedSite.value.siteId)
		)
			setSelectedSite(currentSite.selectedSite);
	}, [currentSite.selectedSite]);

	useEffect(() => {
		if (selectedSite != null) {
			const tableName = selectedSite.value.table_name;

			GYAxios.get(`/general_info/inverter/kwp/site/${tableName}`).then(
				(res) => {
					const kwpValues = res.data.error == null ? res.data : [];

					const kwpEntries = kwpValues.map((obj) => getKWPValues(obj));
					const kwp = Object.fromEntries(kwpEntries);

					setKWPValues(kwp);
				}
			);
		}
	}, [selectedSite]);

	useEffect(() => {
		if (kWPValues != null) handleGraphData(props.mainGraphData);
	}, [props.mainGraphData, kWPValues, selectAll]);

	return (
		<>
			{graphOpts != null ? (
				<>
					<ReactEcharts
						style={{ height: "600px", padding: "20px 0px 20px 0px" }}
						option={graphOpts}
						notMerge={true}
						lazyUpdate={true}
					/>
					<div className="centralised" style={{ width: "20%" }}>
						<Col md={6}>
							<label className="radioLabel">
								<input
									className="radioInput"
									name="selectRadio"
									type="radio"
									value="select"
									onChange={() => {
										setSelectAll(false);
									}}
								/>
								<p className="radioText">Deselect All</p>
							</label>
						</Col>
						<Col md={6}>
							<label className="radioLabel">
								<input
									className="radioInput"
									name="selectRadio"
									type="radio"
									value="select"
									onChange={() => {
										setSelectAll(true);
									}}
									defaultChecked
								/>
								<p className="radioText">Select All</p>
							</label>
						</Col>
					</div>
				</>
			) : (
				<></>
			)}
		</>
	);
};

export default MapkwpGraph;
